import { cloneDeep, get, isUndefined, set } from 'lodash';
import priceTagBindings from '@sharedModules/price-tag-bindings';
import priceTagTransforms from '@enums/price-tag-transforms';
import { getTransform } from './transforms';

const { isAllowedPriceTagBinding, isDisplayValueBinding, getDisplayValueKey } = priceTagBindings;

const allowedFields = fields => (fields || []).filter(f => isAllowedPriceTagBinding(f.binding));

// Transformed fields can depend on values written by the plain fields, so they run last.
const orderForWrite = fields => {
    const tierCountFields = fields.filter(f => f.transform === priceTagTransforms.tierCount);
    const plainFields = fields.filter(f => !f.transform);
    const remainingTransforms = fields.filter(
        f => f.transform && f.transform !== priceTagTransforms.tierCount
    );

    // tierCount needs to run before tier value writes so the tier skeleton exists.
    return [...tierCountFields, ...plainFields, ...remainingTransforms];
};

const getSelectedTierCount = ({ fields, values }) => {
    const tierCountField = (fields || []).find(
        field => field.transform === priceTagTransforms.tierCount
    );
    if (!tierCountField) return null;

    const raw = values[tierCountField.key];
    if (isUndefined(raw) || raw === null || raw === '') return null;

    const parsed = Math.floor(Number(raw));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const shouldSkipTierWrite = ({ field, selectedTierCount }) => {
    if (!selectedTierCount || field.transform) return false;

    const match = /^tiers\[([0-9]+)\]\./.exec(field.binding);
    if (!match) return false;

    const tierIndex = Number(match[1]);
    return Number.isFinite(tierIndex) && tierIndex + 1 > selectedTierCount;
};

export const getDefaultValues = fields =>
    allowedFields(fields).reduce((acc, field) => {
        acc[field.key] = isUndefined(field.defaultValue) ? null : field.defaultValue;
        return acc;
    }, {});

/**
 * Writes price tag values into a copy of the preset mechanic template.
 * @returns {{ offerMechanic: Object, displayValues: Object }}
 */
export function applyValuesToMechanic({ template, fields, values, transformContext = {} }) {
    const offerMechanic = cloneDeep(template);
    const displayValues = {};
    const selectedTierCount = getSelectedTierCount({ fields, values });

    orderForWrite(allowedFields(fields)).forEach(field => {
        if (shouldSkipTierWrite({ field, selectedTierCount })) {
            return;
        }

        const rawValue = isUndefined(values[field.key]) ? null : values[field.key];
        const value = getTransform(field.transform).toMechanic(rawValue, {
            ...transformContext,
            offerMechanic,
        });

        if (isDisplayValueBinding(field.binding)) {
            displayValues[getDisplayValueKey(field.binding)] = value;
        } else {
            set(offerMechanic, field.binding, value);
        }
    });

    return { offerMechanic, displayValues };
}

/**
 * Reads price tag values back from a mechanic and the stored display values.
 */
export function readValuesFromMechanic({
    offerMechanic,
    fields,
    displayValues,
    transformContext = {},
}) {
    return allowedFields(fields).reduce((acc, field) => {
        const storedValue = isDisplayValueBinding(field.binding)
            ? get(displayValues, getDisplayValueKey(field.binding))
            : get(offerMechanic, field.binding);
        const value = getTransform(field.transform).fromMechanic(
            isUndefined(storedValue) ? null : storedValue,
            { ...transformContext, offerMechanic }
        );
        acc[field.key] = isUndefined(value) ? null : value;
        return acc;
    }, {});
}

export default { getDefaultValues, applyValuesToMechanic, readValuesFromMechanic };
